import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/pages/admin/components/AdminLayout';
import { supabase } from '@/lib/supabase';

interface HealthLog {
  id: string;
  checked_at: string;
  success: boolean;
  tables_status: Record<string, unknown>;
}

const STORAGE_KEY = 'koorpet_health_check_logs';

function loadLogs(): HealthLog[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveLogs(logs: HealthLog[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(logs.slice(0, 50)));
  } catch { /* ignore */ }
}

export default function SchedulerPage() {
  const [logs, setLogs] = useState<HealthLog[]>(loadLogs);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const runHealthCheck = useCallback(async () => {
    setRunning(true);
    setLastResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('db-health-check', {
        body: {},
      });

      const entry: HealthLog = {
        id: Date.now().toString(),
        checked_at: new Date().toISOString(),
        success: !error && data?.success === true,
        tables_status: data?.tables || {},
      };

      const updated = [entry, ...logs].slice(0, 50);
      setLogs(updated);
      saveLogs(updated);
      setLastResult(entry.success ? 'success' : 'fail');
    } catch {
      const entry: HealthLog = {
        id: Date.now().toString(),
        checked_at: new Date().toISOString(),
        success: false,
        tables_status: { error: 'Network or function error' },
      };
      const updated = [entry, ...logs].slice(0, 50);
      setLogs(updated);
      saveLogs(updated);
      setLastResult('fail');
    } finally {
      setRunning(false);
    }
  }, [logs]);

  const lastSuccess = logs.find((l) => l.success);

  return (
    <AdminLayout title="定时任务">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Info card */}
        <div className="bg-white rounded-2xl border border-[#ede9e3] p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-[#C8714A]/10 rounded-xl flex-shrink-0">
              <i className="ri-timer-line text-xl text-[#C8714A]"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#1a1a1a] mb-1">数据库保活定时任务</h2>
              <p className="text-sm text-[#888] leading-relaxed">
                该定时任务每天凌晨 5:00（UTC+8）自动执行一次，读取数据库各表数据以保持 Supabase 项目处于活跃状态，
                防止因长时间无操作导致项目被暂停。任务仅读取数据，不会写入或修改任何内容。
              </p>
            </div>
          </div>
        </div>

        {/* Status card */}
        <div className="bg-white rounded-2xl border border-[#ede9e3] p-6 md:p-8">
          <h3 className="text-sm font-semibold text-[#555] mb-5 flex items-center gap-2">
            <i className="ri-pulse-line text-[#C8714A]"></i>
            运行状态
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-[#faf8f5] rounded-xl p-4">
              <div className="text-xs text-[#aaa] mb-1">调度状态</div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span className="text-sm font-semibold text-[#1a1a1a]">已配置</span>
              </div>
              <div className="text-xs text-[#aaa] mt-1">每天 05:00 执行</div>
            </div>
            <div className="bg-[#faf8f5] rounded-xl p-4">
              <div className="text-xs text-[#aaa] mb-1">上次成功</div>
              <div className="text-sm font-semibold text-[#1a1a1a]">
                {lastSuccess
                  ? new Date(lastSuccess.checked_at).toLocaleString('zh-CN')
                  : '暂无记录'}
              </div>
              <div className="text-xs text-[#aaa] mt-1">共 {logs.filter((l) => l.success).length} 次成功</div>
            </div>
            <div className="bg-[#faf8f5] rounded-xl p-4">
              <div className="text-xs text-[#aaa] mb-1">检查表数量</div>
              <div className="text-sm font-semibold text-[#1a1a1a]">
                {lastSuccess?.tables_status
                  ? Object.keys(lastSuccess.tables_status).length
                  : '-'} 张表
              </div>
              <div className="text-xs text-[#aaa] mt-1">products / categories / messages / settings</div>
            </div>
          </div>

          <button
            onClick={runHealthCheck}
            disabled={running}
            className="w-full sm:w-auto px-6 py-3 rounded-full bg-[#C8714A] text-white font-semibold text-sm hover:bg-[#b5603b] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {running ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                检查中...
              </>
            ) : (
              <>
                <i className="ri-play-circle-line"></i>
                手动执行一次
              </>
            )}
          </button>

          {lastResult && (
            <div className={`mt-4 px-4 py-3 rounded-xl text-sm flex items-center gap-2 ${
              lastResult === 'success'
                ? 'bg-green-50 border border-green-100 text-green-600'
                : 'bg-red-50 border border-red-100 text-red-500'
            }`}>
              <i className={lastResult === 'success' ? 'ri-check-line' : 'ri-close-line'}></i>
              {lastResult === 'success' ? '数据库健康检查通过，所有表读取正常。' : '检查失败，请查看日志详情。'}
            </div>
          )}
        </div>

        {/* Cron setup card */}
        <div className="bg-white rounded-2xl border border-[#ede9e3] p-6 md:p-8">
          <h3 className="text-sm font-semibold text-[#555] mb-3 flex items-center gap-2">
            <i className="ri-settings-3-line text-[#C8714A]"></i>
            pg_cron 配置
          </h3>
          <p className="text-sm text-[#888] mb-4">
            已在 Supabase 数据库中配置 pg_cron 扩展，每天 05:00 (UTC+8) 自动调用 Edge Function 执行健康检查。
            即北京时间凌晨5点，对应 UTC 时间 21:00（前一天）。
          </p>
          <div className="bg-[#faf8f5] rounded-xl p-4">
            <code className="text-xs text-[#555] block whitespace-pre-wrap break-all">
{`SELECT cron.schedule(
  'db-health-check',
  '0 21 * * *',
  $$
  SELECT net.http_post(
    url:='https://fdxjebyoqhgkrbiofazf.supabase.co/functions/v1/db-health-check',
    headers:='{"Content-Type": "application/json"}'::jsonb,
    body:=''::jsonb
  ) AS request_id;
  $$
);`}
            </code>
          </div>
        </div>

        {/* Execution log */}
        <div className="bg-white rounded-2xl border border-[#ede9e3] p-6 md:p-8">
          <h3 className="text-sm font-semibold text-[#555] mb-5 flex items-center gap-2">
            <i className="ri-history-line text-[#C8714A]"></i>
            执行日志
          </h3>

          {logs.length === 0 ? (
            <div className="text-center py-10 text-sm text-[#aaa]">
              <i className="ri-inbox-line text-3xl block mb-2"></i>
              暂无执行记录，点击上方按钮手动执行一次
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm ${
                    log.success
                      ? 'bg-green-50 border border-green-100'
                      : 'bg-red-50 border border-red-100'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${log.success ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className={`font-medium flex-shrink-0 ${log.success ? 'text-green-700' : 'text-red-500'}`}>
                      {log.success ? '通过' : '失败'}
                    </span>
                    <span className="text-[#888] truncate">
                      {new Date(log.checked_at).toLocaleString('zh-CN')}
                    </span>
                  </div>
                  <div className="text-xs text-[#aaa] flex-shrink-0 ml-3">
                    {Object.keys(log.tables_status).length} 表
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}